import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination renderer. Shows up to 7 pages with ellipsis logic.
 * Props:
 *  - page: current 1-indexed page
 *  - totalPages: integer >= 1
 *  - onChange: (newPage) => void
 */
export default function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  // Build the list of page numbers to render
  const pages = [];
  const span = 2; // pages to show on each side of current
  const add = (n) => { if (!pages.includes(n) && n >= 1 && n <= totalPages) pages.push(n); };

  add(1);
  for (let i = page - span; i <= page + span; i++) add(i);
  add(totalPages);
  pages.sort((a, b) => a - b);

  // Insert ellipses
  const out = [];
  for (let i = 0; i < pages.length; i++) {
    out.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) out.push("…");
  }

  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <nav
      className="flex items-center justify-center gap-2 py-10 select-none"
      aria-label="Paginação"
      data-testid="pagination"
    >
      <button
        onClick={() => go(page - 1)}
        disabled={page === 1}
        className="flex items-center gap-1 h-10 px-3 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        data-testid="page-prev"
        aria-label="Página anterior"
      >
        <ChevronLeft size={16} /> <span className="hidden sm:inline text-sm">Anterior</span>
      </button>
      {out.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="px-2 text-zinc-600">…</span>
        ) : (
          <button
            key={p}
            onClick={() => go(p)}
            aria-current={p === page ? "page" : undefined}
            data-testid={`page-${p}`}
            className={`min-w-[40px] h-10 px-3 rounded-full text-sm font-bold transition-all ${
              p === page
                ? "bg-[#FF007F] text-white shadow-lg shadow-[#FF007F]/30"
                : "border border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300"
            }`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => go(page + 1)}
        disabled={page === totalPages}
        className="flex items-center gap-1 h-10 px-3 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-zinc-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        data-testid="page-next"
        aria-label="Próxima página"
      >
        <span className="hidden sm:inline text-sm">Próxima</span> <ChevronRight size={16} />
      </button>
    </nav>
  );
}
